import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import {
	generateMediaId,
	upsertOfflineVideo,
	updateDownloadProgress,
	getOfflineVideo,
	isCompletedStatus,
	type OfflineVideoMetadata,
} from "./storage/offlineVideosStorage";
import {
	saveCheckpoint,
	loadCheckpoint,
	clearCheckpoint,
	saveHLSCheckpoint,
	loadHLSCheckpoint,
	clearHLSCheckpoint,
	type HLSDownloadCheckpoint,
} from "./storage/downloadCheckpoints";
import { videoLog } from "@/components/VideoDownloadDebugger";
import type { ProductDataMultimedia, ProductTypeMultimedia } from "@/pages/Workshop/types";

// Cache for blob URLs (web platform only)
const audioBlobCache = new Map<string, string>();

/**
 * Clear blob URL cache (useful for memory management)
 */
export const clearAudioBlobCache = (): void => {
	audioBlobCache.forEach((blobUrl) => {
		URL.revokeObjectURL(blobUrl);
	});
	audioBlobCache.clear();
	console.log("🧹 [Web] Audio blob cache cleared");
};

/**
 * Clear specific audio from cache
 */
export const clearAudioBlobFromCache = (audioId: string): void => {
	const blobUrl = audioBlobCache.get(audioId);
	if (blobUrl) {
		URL.revokeObjectURL(blobUrl);
		audioBlobCache.delete(audioId);
		console.log("🧹 [Web] Cleared audio from cache:", audioId);
	}
};

/**
 * Active downloads tracker for pause/cancel functionality
 */
interface ActiveDownload {
	controller: AbortController;
	audioId: string;
}

const activeDownloads = new Map<string, ActiveDownload>();

/**
 * Helper: Convert ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
	return new Promise((resolve, reject) => {
		const blob = new Blob([buffer]);
		const reader = new FileReader();
		reader.onloadend = (): void => {
			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1];
				resolve(base64 || '');
			} else {
				reject(new Error('Failed to convert ArrayBuffer to base64'));
			}
		};
		reader.onerror = (): void => {
			reject(new Error('FileReader error'));
		};
		reader.readAsDataURL(blob);
	});
}

/**
 * Helper: Convert Blob to Base64
 */
function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = (): void => {
			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1];
				resolve(base64 || '');
			} else {
				reject(new Error('Failed to convert blob to base64'));
			}
		};
		reader.onerror = (): void => {
			reject(new Error('FileReader error'));
		};
		reader.readAsDataURL(blob);
	});
}

// ============================================
// HLS M3U8 Parsing Functions
// ============================================

/**
 * Resolve relative segment URL against base URL
 * Handles Vimeo's relative paths like ../../../../../remux/avf/.../segment.ts?...
 */
function resolveSegmentUrl(baseUrl: string, relativePath: string): string {
	// If already absolute URL, return as-is
	if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
		return relativePath;
	}
	
	try {
		const base = new URL(baseUrl);
		
		// Get base path without the filename (media.m3u8)
		const basePath = base.pathname.substring(0, base.pathname.lastIndexOf('/'));
		const pathParts = basePath.split('/').filter(Boolean);
		
		// Handle query params in relative path
		let queryString = '';
		let cleanRelativePath = relativePath;
		
		const queryIndex = relativePath.indexOf('?');
		if (queryIndex !== -1) {
			queryString = relativePath.substring(queryIndex);
			cleanRelativePath = relativePath.substring(0, queryIndex);
		}
		
		// Process relative path parts
		const relativeParts = cleanRelativePath.split('/');
		
		for (const part of relativeParts) {
			if (part === '..') {
				pathParts.pop();
			} else if (part !== '.' && part !== '') {
				pathParts.push(part);
			}
		}
		
		const finalPath = '/' + pathParts.join('/');
		return `${base.origin}${finalPath}${queryString}`;
	} catch {
		// Fallback: try simple concatenation
		const baseDirectory = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
		return baseDirectory + relativePath;
	}
}

/**
 * Parse m3u8 playlist and extract segment URLs
 * Returns array of absolute segment URLs
 */
function parseM3u8Playlist(m3u8Content: string, baseUrl: string): {
	segments: Array<string>;
	totalDuration: number;
} {
	const lines = m3u8Content.split('\n');
	const segments: Array<string> = [];
	let totalDuration = 0;
	
	for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
		const line = lines[lineIndex]?.trim();
		
		// Extract duration from #EXTINF
		if (line?.startsWith('#EXTINF:')) {
			const durationMatch = line.match(/#EXTINF:([\d.]+)/);
			if (durationMatch?.[1]) {
				totalDuration += parseFloat(durationMatch[1]);
			}
		}
		
		// Non-comment, non-empty line after metadata is a segment URL
		if (line && !line.startsWith('#') && line.length > 0) {
			const absoluteUrl = resolveSegmentUrl(baseUrl, line);
			segments.push(absoluteUrl);
		}
	}
	
	console.log(`📋 [HLS] Parsed m3u8: ${segments.length} segments, ${totalDuration.toFixed(1)}s total duration`);
	
	return { segments, totalDuration };
}

/**
 * Check if URL is an HLS playlist (m3u8)
 */
function isHLSUrl(url: string): boolean {
	return url.includes('.m3u8') || url.includes('application/x-mpegurl');
}

// ============================================
// HLS Audio Download Function
// ============================================

/**
 * Download HLS audio stream (m3u8 + segments)
 * Parses m3u8 playlist, downloads all segments, combines them into a single file
 * Supports pause/resume via segment checkpoints
 */
export const downloadAudioHLS = async (
	courseClassesId: string,
	productData: ProductDataMultimedia,
	audioName: string,
	duration: number,
	hlsPlaylistUrl: string,
	estimatedSizeMB: number,
	vimeoId?: number,
	onProgress?: (progress: number) => void
): Promise<void> => {
	const { productType, productId } = productData;
	// Use base key format (without _audio suffix) - mediaType field distinguishes audio from video
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const fileName = `audio_${audioId}.ts`;
	const filePath = `offline_audio/${fileName}`;
	
	// Batch size for memory efficiency (50 segments ≈ 5 minutes of audio)
	const SEGMENT_BATCH_SIZE = 50;
	const WRITE_CHUNK_SIZE = 512 * 1024; // 512KB per write (Android bridge safe)
	
	videoLog.info("Download", "Starting HLS audio download", {
		audioName,
		hlsPlaylistUrl,
		estimatedSizeMB,
	}, audioId);
	
	// Create AbortController for this download
	const controller = new AbortController();
	activeDownloads.set(audioId, { controller, audioId });
	
	try {
		// ============================================
		// STEP 1: Parse m3u8 playlist (0-5% progress)
		// ============================================
		console.log("📥 [HLS] Step 1/3: Fetching and parsing m3u8 playlist...");
		onProgress?.(2);
		
		const m3u8Response = await fetch(hlsPlaylistUrl, {
			signal: controller.signal,
		});
		
		if (!m3u8Response.ok) {
			throw new Error(`Failed to fetch m3u8 playlist: ${m3u8Response.status}`);
		}
		
		const m3u8Content = await m3u8Response.text();
		const { segments, totalDuration } = parseM3u8Playlist(m3u8Content, hlsPlaylistUrl);
		
		if (!segments || segments.length === 0) {
			throw new Error('No segments found in HLS playlist');
		}
		
		console.log(`✅ [HLS] Found ${segments.length} audio segments (${totalDuration.toFixed(1)}s)`);
		videoLog.info("Download", `Found ${segments.length} audio segments`, {
			segmentCount: segments.length,
			totalDuration: totalDuration.toFixed(1),
		}, audioId);
		
		onProgress?.(5);
		
		// ============================================
		// STEP 2: Create metadata and prepare
		// ============================================
		const metadata: OfflineVideoMetadata = {
			videoId: audioId,
			courseClassesId,
			productData,
			videoName: audioName,
			duration: duration || totalDuration,
			mediaType: "audio",
			videoSource: "vimeo_source",
			vimeoId,
			originalVideoUrl: hlsPlaylistUrl,
			selectedResolution: "audio",
			downloadUrl: hlsPlaylistUrl,
			fileSizeMB: estimatedSizeMB,
			localFilePath: filePath,
			fileName,
			status: "downloading",
			downloadProgress: 5,
			downloadStartedAt: Date.now(),
		};
		
		await upsertOfflineVideo(metadata);
		
		// Ensure directory exists
		try {
			await Filesystem.mkdir({
				path: "offline_audio",
				directory: Directory.Data,
				recursive: true,
			});
		} catch {
			// Directory already exists
		}
		
		// ============================================
		// STEP 3: Check for resume checkpoint
		// ============================================
		let startSegmentIndex = 0;
		const checkpoint = await loadHLSCheckpoint(audioId);
		
		if (checkpoint && checkpoint.downloadedSegments > 0) {
			startSegmentIndex = checkpoint.downloadedSegments;
			console.log(`🔄 [HLS] Resuming from segment ${startSegmentIndex}/${segments.length}`);
			videoLog.info("Resume", `Resuming HLS download from segment ${startSegmentIndex}`, {
				startSegmentIndex,
				totalSegments: segments.length,
			}, audioId);
			
			// Update progress based on checkpoint
			const resumeProgress = 5 + (startSegmentIndex / segments.length) * 85;
			onProgress?.(resumeProgress);
			await updateDownloadProgress(audioId, resumeProgress, "downloading");
		} else {
			// Fresh download - delete existing file if any
			try {
				await Filesystem.deleteFile({
					path: filePath,
					directory: Directory.Data,
				});
			} catch {
				// File doesn't exist, that's fine
			}
		}
		
		// ============================================
		// STEP 4: Download segments in batches (5-90% progress)
		// ============================================
		console.log(`📥 [HLS] Step 2/3: Downloading ${segments.length - startSegmentIndex} segments...`);
		
		let currentSegmentIndex = startSegmentIndex;
		let isFirstWrite = startSegmentIndex === 0;
		
		/* eslint-disable no-await-in-loop */
		while (currentSegmentIndex < segments.length) {
			// Determine batch end
			const batchEnd = Math.min(currentSegmentIndex + SEGMENT_BATCH_SIZE, segments.length);
			const batchSegments: Array<Uint8Array> = [];
			
			// Download batch of segments
			for (let segmentIndex = currentSegmentIndex; segmentIndex < batchEnd; segmentIndex++) {
				const segmentUrl = segments[segmentIndex];
				
				if (!segmentUrl) {
					console.warn(`[HLS] Skipping invalid segment at index ${segmentIndex}`);
					continue;
				}
				
				// Download segment
				const segmentResponse = await fetch(segmentUrl, {
					signal: controller.signal,
				});
				
				if (!segmentResponse.ok) {
					throw new Error(`Failed to download segment ${segmentIndex}: ${segmentResponse.status}`);
				}
				
				const segmentData = await segmentResponse.arrayBuffer();
				batchSegments.push(new Uint8Array(segmentData));
				
				// Update progress during download
				const downloadProgress = 5 + ((segmentIndex + 1) / segments.length) * 85;
				
				// Log every 20 segments
				if ((segmentIndex + 1) % 20 === 0 || segmentIndex === segments.length - 1) {
					console.log(`📥 [HLS] Downloaded segment ${segmentIndex + 1}/${segments.length} (${Math.round(downloadProgress)}%)`);
					videoLog.info("Download", `Segment ${segmentIndex + 1}/${segments.length}`, {
						progress: Math.round(downloadProgress),
					}, audioId);
				}
				
				onProgress?.(downloadProgress);
				
				// Update storage progress less frequently (every 10 segments)
				if ((segmentIndex + 1) % 10 === 0) {
					await updateDownloadProgress(audioId, downloadProgress, "downloading");
				}
			}
			
			// ============================================
			// STEP 4b: Write batch to file
			// ============================================
			if (batchSegments.length > 0) {
				// Combine batch segments into a single blob
				const batchBlob = new Blob(batchSegments as Array<BlobPart>, { type: 'audio/mp4' });
				
				// Write in chunks (for Android bridge compatibility)
				let blobOffset = 0;
				const blobSize = batchBlob.size;
				
				while (blobOffset < blobSize) {
					const chunkEnd = Math.min(blobOffset + WRITE_CHUNK_SIZE, blobSize);
					const chunk = batchBlob.slice(blobOffset, chunkEnd);
					const base64Chunk = await blobToBase64(chunk);
					
					if (isFirstWrite) {
						await Filesystem.writeFile({
							path: filePath,
							data: base64Chunk,
							directory: Directory.Data,
						});
						isFirstWrite = false;
					} else {
						await Filesystem.appendFile({
							path: filePath,
							data: base64Chunk,
							directory: Directory.Data,
						});
					}
					
					blobOffset = chunkEnd;
				}
				
				// Clear batch from memory
				batchSegments.length = 0;
			}
			
			// Update current position
			currentSegmentIndex = batchEnd;
			
			// Save checkpoint after each batch
			const hlsCheckpoint: HLSDownloadCheckpoint = {
				audioId,
				downloadedSegments: currentSegmentIndex,
				totalSegments: segments.length,
				lastUpdate: Date.now(),
				filePath,
				segmentUrls: segments,
				hlsPlaylistUrl,
			};
			await saveHLSCheckpoint(audioId, hlsCheckpoint);
		}
		/* eslint-enable no-await-in-loop */
		
		// ============================================
		// STEP 5: Finalization (90-100% progress)
		// ============================================
		console.log(`📥 [HLS] Step 3/3: Finalizing...`);
		onProgress?.(92);
		await updateDownloadProgress(audioId, 92, "downloading");
		
		// Verify file was created
		try {
			const stat = await Filesystem.stat({
				path: filePath,
				directory: Directory.Data,
			});
			console.log(`✅ [HLS] Audio file created: ${(stat.size / 1024 / 1024).toFixed(2)} MB`);
			videoLog.success("Download", `Audio file created: ${(stat.size / 1024 / 1024).toFixed(2)} MB`, {
				fileSizeBytes: stat.size,
				fileSizeMB: (stat.size / 1024 / 1024).toFixed(2),
				segmentCount: segments.length,
			}, audioId);
		} catch (error) {
			console.warn('[HLS] Could not verify file size:', error);
		}
		
		// Clear checkpoint and mark as completed
		await clearHLSCheckpoint(audioId);
		
		onProgress?.(100);
		await updateDownloadProgress(audioId, 100, "completed_audio");
		
		console.log(`✅ [HLS] Audio download completed: ${fileName}`);
		videoLog.success("Download", `HLS audio download completed`, {
			fileName,
			segmentCount: segments.length,
		}, audioId);
		
	} catch (error) {
		// Check if this was an intentional abort (pause)
		if (error instanceof Error && error.name === 'AbortError') {
			console.log('⏸️ [HLS] Audio download paused by user');
			videoLog.info("Download", "HLS audio download paused by user", {}, audioId);
			
			// Checkpoint should already be saved
			await updateDownloadProgress(audioId, 0, "paused");
			throw new Error('DOWNLOAD_PAUSED');
		}
		
		// Real error - log and update status
		console.error("❌ [HLS] Audio download error:", error);
		videoLog.error("Download", "HLS audio download failed", {
			error: error instanceof Error ? error.message : String(error),
		}, audioId);
		
		await updateDownloadProgress(audioId, 0, "failed");
		throw error;
	} finally {
		// Clean up active download tracker
		activeDownloads.delete(audioId);
	}
};

/**
 * Pause an active audio download
 */
export const pauseAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	// Use base key format (without _audio suffix)
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const download = activeDownloads.get(audioId);
	
	if (download) {
		console.log(`⏸️ Pausing audio download: ${audioId}`);
		videoLog.warning("Pause", "Pausing audio download by user", { audioId }, audioId);
		
		download.controller.abort();
		activeDownloads.delete(audioId);
		
		await updateDownloadProgress(audioId, 0, "paused");
		console.log(`💾 Partial audio file preserved for resume`);
	} else {
		console.log(`⚠️ No active audio download found for: ${audioId}`);
		const audio = await getOfflineVideo(audioId);
		if (audio && audio.status === "downloading") {
			await updateDownloadProgress(audioId, audio.downloadProgress, "paused");
		}
	}
};

/**
 * Download audio with Range Request support for resume capability
 */
export const downloadAudioWithResume = async (
	courseClassesId: string,
	productData: ProductDataMultimedia,
	audioName: string,
	duration: number,
	audioUrl: string,
	fileSizeMB: number,
	vimeoId?: number,
	onProgress?: (progress: number) => void
): Promise<void> => {
	const { productType, productId } = productData;
	// Use base key format (without _audio suffix) - mediaType field distinguishes audio from video
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const fileName = `audio_${audioId}.ts`;
	const filePath = `offline_audio/${fileName}`;
	
	const NETWORK_CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per network request
	const WRITE_CHUNK_SIZE = 512 * 1024; // 512KB per write (Android bridge safe)
	
	videoLog.info("Download", "Starting audio download with Range support", {
		audioName,
		fileSizeMB,
		networkChunkSizeMB: (NETWORK_CHUNK_SIZE / 1024 / 1024).toFixed(2),
	}, audioId);
	
	// Create AbortController for this download
	const controller = new AbortController();
	activeDownloads.set(audioId, { controller, audioId });

	// Get file size with HEAD request
	let totalSize = 0;
	try {
		const headResponse = await fetch(audioUrl, { method: 'HEAD' });
		const contentLength = headResponse.headers.get('Content-Length');
		totalSize = contentLength ? Number(contentLength) : 0;
		console.log(`📊 Audio file size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
		videoLog.info("Download", `Audio file size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`, {
			totalSize,
		}, audioId);
	} catch (error) {
		console.error('Error getting audio file size:', error);
		videoLog.error("Download", "Error getting audio file size", {
			error: error instanceof Error ? error.message : String(error)
		}, audioId);
		throw error;
	}

	// Create initial metadata
	const metadata: OfflineVideoMetadata = {
		videoId: audioId,
		courseClassesId,
		productData,
		videoName: audioName,
		duration,
		mediaType: "audio",
		videoSource: "vimeo_source",
		vimeoId,
		originalVideoUrl: audioUrl,
		selectedResolution: "audio",
		downloadUrl: audioUrl,
		fileSizeMB: fileSizeMB || (totalSize / 1024 / 1024),
		localFilePath: filePath,
		fileName,
		status: "downloading",
		downloadProgress: 0,
		downloadStartedAt: Date.now(),
	};

	await upsertOfflineVideo(metadata);

	// Ensure directory exists
	try {
		await Filesystem.mkdir({
			path: "offline_audio",
			directory: Directory.Data,
			recursive: true,
		});
	} catch (error) {
		console.log("Directory already exists or error:", error);
	}

	// Load checkpoint if exists (resume previous download)
	const checkpoint = await loadCheckpoint(audioId);
	const downloadedBytes = checkpoint?.downloadedBytes || 0;
	
	if (checkpoint && checkpoint.downloadedBytes > 0) {
		console.log(`🔄 Resuming audio download from ${(downloadedBytes / 1024 / 1024).toFixed(2)} MB`);
		videoLog.info("Resume", `Resuming from checkpoint`, {
			downloadedBytes,
			downloadedMB: (downloadedBytes / 1024 / 1024).toFixed(2),
		}, audioId);
		
		const resumeProgress = (downloadedBytes / totalSize) * 90;
		onProgress?.(resumeProgress);
		await updateDownloadProgress(audioId, resumeProgress, "downloading");
	}

	try {
		let currentPosition = downloadedBytes;
		let lastProgressUpdate = 0;

		// Download in chunks with Range requests
		/* eslint-disable no-await-in-loop */
		while (currentPosition < totalSize) {
			const end = Math.min(currentPosition + NETWORK_CHUNK_SIZE - 1, totalSize - 1);
			
			console.log(`📥 Downloading audio chunk: ${(currentPosition / 1024 / 1024).toFixed(2)} - ${(end / 1024 / 1024).toFixed(2)} MB`);
			
			const response = await fetch(audioUrl, {
				headers: {
					'Range': `bytes=${currentPosition}-${end}`
				},
				signal: controller.signal
			});
			
			if (response.status !== 206 && response.status !== 200) {
				throw new Error(`Unexpected response status: ${response.status}`);
			}
			
			const chunkArrayBuffer = await response.arrayBuffer();
			const chunkSize = chunkArrayBuffer.byteLength;
			
			// Write chunk to file in smaller pieces
			let chunkOffset = 0;
			while (chunkOffset < chunkSize) {
				const writeEnd = Math.min(chunkOffset + WRITE_CHUNK_SIZE, chunkSize);
				const writeChunk = chunkArrayBuffer.slice(chunkOffset, writeEnd);
				const base64Chunk = await arrayBufferToBase64(writeChunk);
				
				if (currentPosition === 0 && chunkOffset === 0) {
					await Filesystem.writeFile({
						path: filePath,
						data: base64Chunk,
						directory: Directory.Data,
					});
				} else {
					await Filesystem.appendFile({
						path: filePath,
						data: base64Chunk,
						directory: Directory.Data,
					});
				}
				
				chunkOffset = writeEnd;
			}
			
			currentPosition = end + 1;
			
			const downloadProgress = (currentPosition / totalSize) * 100;
			const totalProgress = downloadProgress * 0.9;
			
			if (Math.floor(totalProgress) - lastProgressUpdate >= 1) {
				lastProgressUpdate = Math.floor(totalProgress);
				onProgress?.(totalProgress);
				await updateDownloadProgress(audioId, totalProgress, "downloading");
			}
			
			// Save checkpoint every 10MB
			if (currentPosition % (10 * 1024 * 1024) < NETWORK_CHUNK_SIZE) {
				await saveCheckpoint(audioId, {
					videoId: audioId,
					downloadedBytes: currentPosition,
					totalSize,
					lastUpdate: Date.now(),
					filePath
				});
			}
		}
		/* eslint-enable no-await-in-loop */

		console.log(`✅ Audio download completed: ${fileName}`);
		videoLog.success("Download", `Audio download completed`, {
			fileName,
			totalSize,
		}, audioId);
		
		// Finalization
		onProgress?.(95);
		await updateDownloadProgress(audioId, 95, "downloading");
		
		// Clear checkpoint and mark as completed
		await clearCheckpoint(audioId);
		await updateDownloadProgress(audioId, 100, "completed_audio");
		onProgress?.(100);
		
	} catch (error) {
		if (error instanceof Error && error.name === 'AbortError') {
			console.log('⏸️ Audio download paused by user');
			videoLog.info("Download", "Audio download paused by user", {}, audioId);
			throw new Error('DOWNLOAD_PAUSED');
		}
		
		console.error("❌ Audio download error:", error);
		videoLog.error("Download", "Audio download failed", {
			error: error instanceof Error ? error.message : String(error),
		}, audioId);
		
		await saveCheckpoint(audioId, {
			videoId: audioId,
			downloadedBytes: downloadedBytes,
			totalSize,
			lastUpdate: Date.now(),
			filePath
		});
		await updateDownloadProgress(audioId, (downloadedBytes / totalSize) * 90, "paused");
		throw error;
	} finally {
		activeDownloads.delete(audioId);
	}
};

/**
 * Resume a paused audio download
 * Automatically detects HLS vs direct file download and uses appropriate method
 */
export const resumeAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string,
	onProgress?: (progress: number) => void
): Promise<void> => {
	// Use base key format (without _audio suffix)
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const audio = await getOfflineVideo(audioId);
	
	if (!audio || audio.mediaType !== "audio") {
		videoLog.error("Resume", "Audio metadata not found", { audioId }, audioId);
		throw new Error('Audio not found');
	}
	
	if (audio.status !== 'paused') {
		console.warn('Audio is not in paused state');
		return;
	}
	
	videoLog.info("Resume", "Resuming paused audio download", {
		audioName: audio.videoName,
		downloadUrl: audio.downloadUrl,
	}, audioId);
	
	// Detect if this is an HLS download (m3u8) or direct file
	if (isHLSUrl(audio.downloadUrl)) {
		console.log(`🔄 [Resume] Detected HLS download, using downloadAudioHLS`);
		await downloadAudioHLS(
			audio.courseClassesId,
			audio.productData,
			audio.videoName,
			audio.duration,
			audio.downloadUrl,
			audio.fileSizeMB,
			audio.vimeoId,
			onProgress
		);
	} else {
		console.log(`🔄 [Resume] Detected direct file download, using downloadAudioWithResume`);
		await downloadAudioWithResume(
			audio.courseClassesId,
			audio.productData,
			audio.videoName,
			audio.duration,
			audio.downloadUrl,
			audio.fileSizeMB,
			audio.vimeoId,
			onProgress
		);
	}
};

/**
 * Cancel audio download and cleanup all files
 */
export const cancelAudioDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	// Use base key format (without _audio suffix)
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const download = activeDownloads.get(audioId);
	
	videoLog.warning("Cancel", "Canceling audio download", { audioId }, audioId);
	
	if (download) {
		download.controller.abort();
		activeDownloads.delete(audioId);
	}
	
	const audio = await getOfflineVideo(audioId);
	
	if (audio?.localFilePath) {
		try {
			await Filesystem.deleteFile({
				path: audio.localFilePath,
				directory: Directory.Data,
			});
			console.log(`🗑️ Deleted partial audio file: ${audio.localFilePath}`);
		} catch (error) {
			console.log("No partial audio file to delete:", error);
		}
	}
	
	// Clear both regular and HLS checkpoints
	await clearCheckpoint(audioId);
	await clearHLSCheckpoint(audioId);
	
	const { removeOfflineVideo } = await import("./storage/offlineVideosStorage");
	await removeOfflineVideo(audioId);
	
	console.log(`✅ Audio download canceled and cleaned up: ${audioId}`);
};

/**
 * Get local audio file URI for playback
 */
export const getLocalAudioUri = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<string | null> => {
	// Use base key format (without _audio suffix) - mediaType field distinguishes audio from video
	const audioId = generateMediaId(productType, productId, courseClassesId);
	const audio = await getOfflineVideo(audioId);

	console.log("🔍 [getLocalAudioUri] Looking for audio:", {
		audioId,
		found: !!audio,
		status: audio?.status,
		platform: Capacitor.getPlatform()
	});

	// Verify this is actually an audio entry with completed status
	if (!audio || audio.mediaType !== "audio" || !isCompletedStatus(audio.status)) {
		videoLog.warning("Playback", "Local audio not found or not completed", {
			found: !!audio,
			status: audio?.status,
		}, audioId);
		return null;
	}

	try {
		const fileUri = await Filesystem.getUri({
			path: audio.localFilePath,
			directory: Directory.Data,
		});
		
		const platform = Capacitor.getPlatform();
		
		if (platform === 'android') {
			const rawPath = fileUri.uri.replace(/^file:\/\//, "");
			const webViewUri = `http://localhost/_capacitor_file_/${rawPath.replace(/^\/+/, "")}`;
			console.log("✅ Android WebView URI (audio):", webViewUri);
			return webViewUri;
		} else if (platform === 'ios') {
			const webViewUri = Capacitor.convertFileSrc(fileUri.uri);
			console.log("✅ iOS WebView URI (audio):", webViewUri);
			return webViewUri;
		} else {
			// Web platform
			try {
				const cachedUrl = audioBlobCache.get(audioId);
				if (cachedUrl) {
					console.log("✅ [Web] Using cached audio blob URL");
					return cachedUrl;
				}
				
				const fileData = await Filesystem.readFile({
					path: audio.localFilePath,
					directory: Directory.Data,
				});
				
				let blob: Blob;
				if (typeof fileData.data === 'string') {
					const response = await fetch(`data:audio/mp4;base64,${fileData.data}`);
					blob = await response.blob();
				} else {
					blob = fileData.data;
				}
				
				const blobUrl = URL.createObjectURL(blob);
				audioBlobCache.set(audioId, blobUrl);
				
				console.log("✅ Web platform - created and cached audio blob URL");
				return blobUrl;
			} catch (error) {
				console.error("❌ [Web] Error creating audio blob URL:", error);
				return null;
			}
		}
	} catch (error) {
		console.error("Error getting local audio URI:", error);
		return null;
	}
};
